import {
  PLATFORM_LABELS,
  PITCH_FORMAT_LABELS,
  PITCH_COMMISSION_TYPE_LABELS,
  USAGE_RIGHTS_LABELS,
} from '@affily/shared/constants';
import { describeRate } from '@affily/shared/commission';

const sectionTitle = 'mb-1 text-2xs font-semibold uppercase tracking-wider text-ink-faint';

/** "1 Jun 2027 – 31 Aug 2027", "From 1 Jun 2027", or null with neither. */
function describeDates(startsAt, endsAt) {
  const fmt = (d) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  if (startsAt && endsAt) return `${fmt(startsAt)} – ${fmt(endsAt)}`;
  if (startsAt) return `From ${fmt(startsAt)}`;
  if (endsAt) return `Until ${fmt(endsAt)}`;
  return null;
}

/**
 * A pitch as the brand reads it: the brief, what the creator would make, and
 * what they ask for in return. The form's preview, the creator's list and the
 * brand's inbox all show this same block, so the creator sees exactly what the
 * brand will.
 */
export function PitchSummary({ pitch }) {
  const dates = describeDates(pitch.startsAt, pitch.endsAt);
  const ask =
    pitch.commissionType === 'gift_only'
      ? PITCH_COMMISSION_TYPE_LABELS.gift_only
      : (pitch.commissionType === 'percent_of_sale' ? pitch.rateBps : pitch.flatAmountCents)
        ? describeRate(pitch)
        : PITCH_COMMISSION_TYPE_LABELS[pitch.commissionType];

  return (
    <div className="space-y-4 text-sm">
      {pitch.brief && <p className="whitespace-pre-line text-ink">{pitch.brief}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <section>
          <h4 className={sectionTitle}>What they’ll make</h4>
          {pitch.deliverables?.length ? (
            <ul className="space-y-0.5">
              {pitch.deliverables.map((d, i) => (
                <li key={i}>
                  {d.count} × {PITCH_FORMAT_LABELS[d.format] ?? d.format} on{' '}
                  {PLATFORM_LABELS[d.platform] ?? d.platform}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-faint">—</p>
          )}
          {dates && <p className="mt-1 text-ink-muted">{dates}</p>}
        </section>

        <section>
          <h4 className={sectionTitle}>What they ask for</h4>
          <p className="font-medium tabular-nums">{ask}</p>
          {pitch.productNote && (
            <p className="text-ink-muted">
              {pitch.commissionType === 'gift_only' ? '' : 'Plus: '}
              {pitch.productNote}
            </p>
          )}
        </section>

        <section>
          <h4 className={sectionTitle}>Use of the content</h4>
          <p className="text-ink-muted">
            {USAGE_RIGHTS_LABELS[pitch.usageRights ?? 'none']}
            {pitch.usageRights && pitch.usageRights !== 'none' && pitch.usageMonths
              ? `, for ${pitch.usageMonths} month${pitch.usageMonths === 1 ? '' : 's'}`
              : ''}
          </p>
        </section>

        <section>
          <h4 className={sectionTitle}>Exclusivity</h4>
          <p className="text-ink-muted">
            {pitch.exclusivityDays
              ? `No competing brand for ${pitch.exclusivityDays} days`
              : 'None'}
          </p>
        </section>
      </div>

      {pitch.notes && (
        <section>
          <h4 className={sectionTitle}>Other conditions</h4>
          <p className="whitespace-pre-line text-ink-muted">{pitch.notes}</p>
        </section>
      )}
    </div>
  );
}
